/**
 * Local PPW pricing from Excel fallback tables (Enfin / LightReach).
 * Logic: PPW_Worked_Examples — slope/intercept by rate+escalator; interpolate rate only.
 */
import tables from '../data/preContractPpwTables.json';
import {
    countIncludedModules,
    estimateLayoutAnnualKwh,
} from './preContractModuleLayout';
import { preContractSystemSizeKw, preContractSystemUsageKwh } from './preContractPricing';
import type { PreContractIntakeForm, PreContractRoofModuleLayout } from './preContractTypes';

export type PpwPartnerId = 'Enfin' | 'LightReach';

export type PpwTableRecord = {
    rate: number;
    escalator: number;
    slope: number;
    intercept: number;
};

export type PpwPartnerQuote = {
    partner: PpwPartnerId;
    available: boolean;
    reason?: string;
    escalator: number;
    rate: number;
    interpolated: boolean;
    slope: number | null;
    intercept: number | null;
    ppw: number | null;
    epc: number | null;
    /** Rough year-1 PPA monthly ≈ rate × annual kWh / 12 (until partner APIs are wired). */
    estimatedMonthly: number | null;
};

/** `contract-api` = PPWDetails rows; `excel-fallback` = bundled Excel tables. */
export type PpwQuoteSource = 'excel-fallback' | 'contract-api';

export type PpwLiveQuoteResult = {
    state: string;
    rate: number;
    /** Form all-in utility rate (comparison only — quoting uses target/min PPA). */
    allInRate: number;
    /** Form Target/Min PPA rate used as the default quote selection. */
    targetPpaRate: number;
    escalator: number;
    multiplier: number;
    systemKw: number;
    systemWatts: number;
    annualProductionKwh: number;
    source: PpwQuoteSource;
    availableEscalators: number[];
    /** Unique PPA rates from Excel tables for this state (optionally at active escalator). */
    availableRates: number[];
    quotes: PpwPartnerQuote[];
};

type PartnerTables = Record<string, { sheet: string; records: PpwTableRecord[] }>;

const PARTNER_TABLES = tables.partners as {
    Enfin: PartnerTables;
    LightReach: PartnerTables;
};

const DEFAULT_ESCALATOR = 0.0299;

function parseMoneyRate(raw: string | number | null | undefined): number {
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    const n = Number(String(raw ?? '').replace(/[$,\s]/g, '').trim());
    return Number.isFinite(n) && n > 0 ? n : 0;
}

function nearlyEqual(a: number, b: number, eps = 1e-6): boolean {
    return Math.abs(a - b) <= eps;
}

function uniqueSorted(values: number[]): number[] {
    const out: number[] = [];
    values
        .slice()
        .sort((a, b) => a - b)
        .forEach((v) => {
            if (!out.some((x) => nearlyEqual(x, v))) out.push(v);
        });
    return out;
}

/** Resolve production multiplier from module shade data. */
export function resolveModuleMultiplier(cell: {
    multiplier?: number;
    solarAccess: number;
}): number | null {
    const multiplier = Number(cell.multiplier);
    if (Number.isFinite(multiplier) && multiplier > 0) return multiplier;
    const sa = Number(cell.solarAccess);
    if (Number.isFinite(sa) && sa > 0 && sa <= 100) return sa / 100;
    return null;
}

/** Average production multiplier for included modules on one roof face. */
export function averageRoofIncludedMultiplier(
    layout: PreContractRoofModuleLayout,
): number | null {
    return roofIncludedMultiplierDetail(layout).multiplier;
}

/** Roof multiplier detail for included modules (matches oneDRAW roofMultiplier logic). */
export function roofIncludedMultiplierDetail(layout: PreContractRoofModuleLayout): {
    multiplier: number | null;
    meanSolarAccess: number | null;
    includedCount: number;
} {
    const present = layout.modules.filter((c): c is NonNullable<typeof c> => Boolean(c));
    const included = present.filter((cell) => cell.included);
    if (!included.length) {
        return { multiplier: null, meanSolarAccess: null, includedCount: 0 };
    }

    const bandMap = new Map<number, number>();
    present.forEach((cell) => {
        const sa = Number(cell.solarAccess);
        const mult = Number(cell.multiplier);
        if (Number.isFinite(sa) && sa > 0 && Number.isFinite(mult) && mult > 0) {
            bandMap.set(Math.floor(sa), mult);
        }
    });

    const saValues = included
        .map((cell) => Number(cell.solarAccess))
        .filter((sa) => Number.isFinite(sa) && sa > 0);
    const meanSolarAccess =
        saValues.length > 0 ? saValues.reduce((sum, sa) => sum + sa, 0) / saValues.length : null;

    let multSum = 0;
    let multCount = 0;
    included.forEach((cell) => {
        const value = resolveModuleMultiplier(cell);
        if (value == null) return;
        multSum += value;
        multCount += 1;
    });
    if (multCount > 0) {
        return {
            multiplier: Math.round((multSum / multCount) * 1000) / 1000,
            meanSolarAccess,
            includedCount: included.length,
        };
    }

    if (meanSolarAccess != null) {
        const fromBand = bandMap.get(Math.floor(meanSolarAccess));
        if (fromBand != null) {
            return {
                multiplier: Math.round(fromBand * 1000) / 1000,
                meanSolarAccess,
                includedCount: included.length,
            };
        }
        return {
            multiplier: Math.round((meanSolarAccess / 100) * 1000) / 1000,
            meanSolarAccess,
            includedCount: included.length,
        };
    }

    return {
        multiplier: null,
        meanSolarAccess,
        includedCount: included.length,
    };
}

/** Average production multiplier across included modules (falls back to SA/100). */
export function averageIncludedMultiplier(
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
): number | null {
    let weightedSum = 0;
    let weightTotal = 0;
    layouts.forEach((layout) => {
        if (roofSelections[layout.roofIndex] === false) return;
        layout.modules.forEach((cell) => {
            if (!cell?.included) return;
            const mult = resolveModuleMultiplier(cell);
            if (mult == null) return;
            const weight =
                typeof cell.productionKwh === 'number' && cell.productionKwh > 0
                    ? cell.productionKwh
                    : 1;
            weightedSum += mult * weight;
            weightTotal += weight;
        });
    });
    if (weightTotal <= 0) return null;
    return Math.round((weightedSum / weightTotal) * 1000) / 1000;
}

function resolveStateCode(form: PreContractIntakeForm): string {
    const state = String(form.installState || '')
        .trim()
        .toUpperCase();
    if (!state) return '';
    if (state === 'NY') {
        const city = String(form.installCity || '').trim().toLowerCase();
        if (city.includes('new york') || city === 'nyc' || city.includes('brooklyn') || city.includes('queens')) {
            return 'NYC';
        }
    }
    return state;
}

function recordsFor(partner: PpwPartnerId, state: string): PpwTableRecord[] {
    const table = PARTNER_TABLES[partner]?.[state];
    if (table?.records?.length) return table.records;
    // NYC designs can fall back to NY LightReach/Enfin when NYC sheet missing for Enfin
    if (state === 'NYC') {
        const ny = PARTNER_TABLES[partner]?.NY;
        if (ny?.records?.length) return ny.records;
    }
    return [];
}

function escalatorsForState(state: string): number[] {
    const all: number[] = [];
    (['Enfin', 'LightReach'] as PpwPartnerId[]).forEach((partner) => {
        recordsFor(partner, state).forEach((row) => all.push(row.escalator));
    });
    return uniqueSorted(all);
}

function ratesForState(state: string, escalator?: number): number[] {
    const all: number[] = [];
    (['Enfin', 'LightReach'] as PpwPartnerId[]).forEach((partner) => {
        recordsFor(partner, state).forEach((row) => {
            if (escalator != null && !nearlyEqual(row.escalator, escalator, 1e-4)) return;
            all.push(row.rate);
        });
    });
    return uniqueSorted(all);
}

function ratesForPartner(partner: PpwPartnerId, state: string): number[] {
    return uniqueSorted(recordsFor(partner, state).map((row) => row.rate));
}

function escalatorsForPartner(partner: PpwPartnerId, state: string): number[] {
    return uniqueSorted(recordsFor(partner, state).map((row) => row.escalator));
}

export type PpwMatrixCell = {
    rate: number;
    escalator: number;
    ppw: number | null;
    epc: number | null;
    estimatedMonthly: number | null;
    available: boolean;
    interpolated: boolean;
};

export type PpwPartnerMatrix = {
    partner: PpwPartnerId;
    rates: number[];
    escalators: number[];
    /** cells[rateIndex][escalatorIndex] */
    cells: PpwMatrixCell[][];
};

/** Full rate × escalator grid for one partner at the current design multiplier/size. */
export function buildPartnerPpwMatrix(input: {
    partner: PpwPartnerId;
    state: string;
    multiplier: number;
    systemWatts: number;
    annualProductionKwh: number;
    /** Extra rate row (e.g. target/min PPA) inserted and sorted with table rates. */
    extraRate?: number | null;
}): PpwPartnerMatrix {
    const { partner, state, multiplier, systemWatts, annualProductionKwh, extraRate } = input;
    const escalators = escalatorsForPartner(partner, state);
    const rateSet = ratesForPartner(partner, state).slice();
    if (extraRate != null && Number.isFinite(extraRate) && extraRate > 0) {
        if (!rateSet.some((r) => nearlyEqual(r, extraRate))) rateSet.push(extraRate);
    }
    const rates = uniqueSorted(rateSet);

    const cells = rates.map((rate) =>
        escalators.map((escalator) => {
            const quote = quotePartnerFromTables({
                partner,
                state,
                rate,
                escalator,
                multiplier,
                systemWatts,
                annualProductionKwh,
            });
            return {
                rate,
                escalator,
                ppw: quote.ppw,
                epc: quote.epc,
                estimatedMonthly: quote.estimatedMonthly,
                available: quote.available,
                interpolated: quote.interpolated,
            };
        }),
    );

    return { partner, rates, escalators, cells };
}

/**
 * Fit slope/intercept for a rate at a fixed escalator.
 * Exact row match, or linear blend between bracketing rates (never across escalator).
 */
export function resolveSlopeIntercept(
    records: PpwTableRecord[],
    rate: number,
    escalator: number,
): { slope: number; intercept: number; interpolated: boolean; rate: number } | null {
    const atEsc = records.filter((row) => nearlyEqual(row.escalator, escalator, 1e-4));
    if (!atEsc.length) return null;

    const exact = atEsc.find((row) => nearlyEqual(row.rate, rate, 1e-6));
    if (exact) {
        return {
            slope: exact.slope,
            intercept: exact.intercept,
            interpolated: false,
            rate: exact.rate,
        };
    }

    const sorted = atEsc.slice().sort((a, b) => a.rate - b.rate);
    let lower: PpwTableRecord | null = null;
    let upper: PpwTableRecord | null = null;
    for (const row of sorted) {
        if (row.rate <= rate) lower = row;
        if (row.rate >= rate && !upper) upper = row;
    }
    if (lower && upper && !nearlyEqual(lower.rate, upper.rate)) {
        const t = (rate - lower.rate) / (upper.rate - lower.rate);
        return {
            slope: lower.slope + t * (upper.slope - lower.slope),
            intercept: lower.intercept + t * (upper.intercept - lower.intercept),
            interpolated: true,
            rate,
        };
    }
    // Clamp to nearest edge row when outside table range
    const edge = lower ?? upper;
    if (!edge) return null;
    return {
        slope: edge.slope,
        intercept: edge.intercept,
        interpolated: false,
        rate: edge.rate,
    };
}

export function calcPpw(slope: number, intercept: number, multiplier: number): number {
    return slope * multiplier + intercept;
}

export function quotePartnerFromTables(input: {
    partner: PpwPartnerId;
    state: string;
    rate: number;
    escalator: number;
    multiplier: number;
    systemWatts: number;
    annualProductionKwh: number;
}): PpwPartnerQuote {
    const { partner, state, rate, escalator, multiplier, systemWatts, annualProductionKwh } = input;
    const records = recordsFor(partner, state);
    if (!records.length) {
        return {
            partner,
            available: false,
            reason: `No ${partner} table for ${state || 'state'}`,
            escalator,
            rate,
            interpolated: false,
            slope: null,
            intercept: null,
            ppw: null,
            epc: null,
            estimatedMonthly: null,
        };
    }
    const escOptions = uniqueSorted(records.map((r) => r.escalator));
    if (!escOptions.some((e) => nearlyEqual(e, escalator, 1e-4))) {
        return {
            partner,
            available: false,
            reason: `Escalator ${(escalator * 100).toFixed(2)}% not offered`,
            escalator,
            rate,
            interpolated: false,
            slope: null,
            intercept: null,
            ppw: null,
            epc: null,
            estimatedMonthly: null,
        };
    }
    const fit = resolveSlopeIntercept(records, rate, escalator);
    if (!fit) {
        return {
            partner,
            available: false,
            reason: 'Could not resolve rate row',
            escalator,
            rate,
            interpolated: false,
            slope: null,
            intercept: null,
            ppw: null,
            epc: null,
            estimatedMonthly: null,
        };
    }
    const ppw = calcPpw(fit.slope, fit.intercept, multiplier);
    const epc = systemWatts > 0 ? ppw * systemWatts : null;
    const estimatedMonthly =
        annualProductionKwh > 0 && rate > 0 ? (rate * annualProductionKwh) / 12 : null;
    return {
        partner,
        available: true,
        escalator,
        rate,
        interpolated: fit.interpolated,
        slope: fit.slope,
        intercept: fit.intercept,
        ppw: Math.round(ppw * 1000) / 1000,
        epc: epc != null ? Math.round(epc * 100) / 100 : null,
        estimatedMonthly:
            estimatedMonthly != null ? Math.round(estimatedMonthly * 100) / 100 : null,
    };
}

/** Live quotes for the module-step rail (Excel fallback until state Excel service is wired). */
export type PreContractPpwMetricsOverride = {
    systemKw?: number | null;
    annualProductionKwh?: number | null;
    multiplier?: number | null;
    moduleCount?: number | null;
};

export function buildPreContractLivePpwQuotes(
    form: PreContractIntakeForm,
    escalator: number = DEFAULT_ESCALATOR,
    rateOverride?: number | null,
    metricsOverride?: PreContractPpwMetricsOverride | null,
): PpwLiveQuoteResult {
    const state = resolveStateCode(form);
    const allInRate = parseMoneyRate(form.utilityRate);
    const targetPpaRate = parseMoneyRate(form.targetMinPpaRate);
    const formSystemKw = preContractSystemSizeKw(form);
    const formAnnualProductionKwh = preContractSystemUsageKwh(form);
    const systemKw =
        metricsOverride?.systemKw != null &&
        Number.isFinite(metricsOverride.systemKw) &&
        metricsOverride.systemKw > 0
            ? metricsOverride.systemKw
            : formSystemKw;
    const systemWatts = Math.round(systemKw * 1000);
    const annualProductionKwh =
        metricsOverride?.annualProductionKwh != null &&
        Number.isFinite(metricsOverride.annualProductionKwh) &&
        metricsOverride.annualProductionKwh > 0
            ? metricsOverride.annualProductionKwh
            : formAnnualProductionKwh;
    /** Excel columns are kWh/kW (÷1000). Used only when module multipliers are unavailable. */
    const yieldMultiplier =
        systemKw > 0 && annualProductionKwh > 0
            ? Math.round((annualProductionKwh / systemKw / 1000) * 1000) / 1000
            : null;
    const overrideMultiplier =
        metricsOverride?.multiplier != null &&
        Number.isFinite(metricsOverride.multiplier) &&
        metricsOverride.multiplier > 0
            ? metricsOverride.multiplier
            : null;
    const moduleMultiplier = averageIncludedMultiplier(
        form.roofModuleLayouts,
        form.roofSelections,
    );
    const multiplier =
        overrideMultiplier ??
        moduleMultiplier ??
        yieldMultiplier ??
        (() => {
            const { averageSolarAccess } = countIncludedModules(
                form.roofModuleLayouts,
                form.roofSelections,
            );
            return averageSolarAccess > 0 ? Math.round((averageSolarAccess / 100) * 1000) / 1000 : 1;
        })();

    const availableEscalators = state ? escalatorsForState(state) : [];
    const activeEscalator = availableEscalators.some((e) => nearlyEqual(e, escalator, 1e-4))
        ? escalator
        : availableEscalators.includes(DEFAULT_ESCALATOR)
          ? DEFAULT_ESCALATOR
          : availableEscalators[availableEscalators.length - 1] ?? escalator;

    const availableRates = state ? ratesForState(state, activeEscalator) : [];
    const rate =
        rateOverride != null && Number.isFinite(rateOverride) && rateOverride > 0
            ? rateOverride
            : targetPpaRate || allInRate;

    const quotes = (['Enfin', 'LightReach'] as PpwPartnerId[]).map((partner) =>
        quotePartnerFromTables({
            partner,
            state,
            rate,
            escalator: activeEscalator,
            multiplier,
            systemWatts,
            annualProductionKwh,
        }),
    );

    return {
        state,
        rate,
        allInRate,
        targetPpaRate,
        escalator: activeEscalator,
        multiplier,
        systemKw,
        systemWatts,
        annualProductionKwh,
        source: 'excel-fallback',
        availableEscalators,
        availableRates,
        quotes,
    };
}

function partnerLabelsMatch(partnerLabel: string, quotePartner: PpwPartnerId): boolean {
    const raw = partnerLabel.trim().toLowerCase().replace(/\s+/g, '');
    const quote = quotePartner.trim().toLowerCase();
    return raw === quote || raw.includes(quote) || quote.includes(raw);
}

/** Match a hub/display partner label to a live PPW quote row. */
export function findLiveQuoteForPartner(
    quotes: PpwPartnerQuote[],
    partnerLabel: string | null | undefined,
): PpwPartnerQuote | null {
    const label = String(partnerLabel ?? '').trim();
    if (!label) return null;
    return quotes.find((quote) => partnerLabelsMatch(label, quote.partner)) ?? null;
}

/** Recompute PPW/EPC from Excel tables using the current layout multiplier. */
export function resolveLivePartnerQuote(
    form: PreContractIntakeForm,
    partnerLabel: string | null | undefined,
    options?: {
        escalator?: number;
        rateOverride?: number | null;
        metricsOverride?: PreContractPpwMetricsOverride | null;
        preferEnfin?: boolean;
    },
): PpwPartnerQuote | null {
    const escalator = options?.escalator ?? DEFAULT_ESCALATOR;
    const result = buildPreContractLivePpwQuotes(
        form,
        escalator,
        options?.rateOverride ?? null,
        options?.metricsOverride ?? null,
    );
    return (
        findLiveQuoteForPartner(result.quotes, partnerLabel) ??
        pickRecommendedPartnerQuote(result.quotes, { preferEnfin: options?.preferEnfin }) ??
        null
    );
}

export function pickRecommendedPartnerQuote(
    quotes: PpwPartnerQuote[],
    options?: { preferEnfin?: boolean },
): PpwPartnerQuote | null {
    const available = quotes.filter(
        (quote) => quote.available && quote.epc != null && Number.isFinite(quote.epc),
    );

    if (options?.preferEnfin) {
        const enfinAvailable = available.find((quote) => quote.partner === 'Enfin');
        if (enfinAvailable) return enfinAvailable;
        const enfinQuote = quotes.find((quote) => quote.partner === 'Enfin');
        if (enfinQuote) return enfinQuote;
    }

    if (available.length === 0) return quotes[0] ?? null;
    return available.reduce((best, quote) =>
        (quote.epc ?? Infinity) < (best.epc ?? Infinity) ? quote : best,
    );
}

export function sortPartnerQuotesForDisplay(
    quotes: PpwPartnerQuote[],
    options?: { preferEnfin?: boolean },
): PpwPartnerQuote[] {
    if (!options?.preferEnfin) return quotes;
    const enfin = quotes.filter((quote) => quote.partner === 'Enfin');
    const rest = quotes.filter((quote) => quote.partner !== 'Enfin');
    return [...enfin, ...rest];
}

export function formatPpaRate(rate: number): string {
    return `$${rate.toFixed(3)}`;
}

export function formatEscalatorPercent(escalator: number): string {
    const pct = escalator * 100;
    const rounded = Math.round(pct * 100) / 100;
    return `${rounded}%`;
}

/** Ensure estimateLayoutAnnualKwh stays available for callers that only import this module. */
export function fallbackProductionFromLayouts(form: PreContractIntakeForm): number {
    return (
        estimateLayoutAnnualKwh({
            layouts: form.roofModuleLayouts,
            roofSelections: form.roofSelections,
            moduleWattage: form.moduleTypeWattage,
        }) ?? 0
    );
}
